/**
 * Typed HTTP client with interceptors.
 *
 * Feature code must call the backend through :func:`http`; never call
 * ``fetch`` or ``axios`` directly. The client injects:
 *   - ``Authorization`` — from the auth token slot (empty in Milestone 1).
 *   - ``X-Workspace-Id`` — from the tenant slot (empty in Milestone 1).
 *   - ``X-Correlation-Id`` — a fresh UUID per request.
 *
 * Errors are mapped to typed :class:`ApiError` instances.
 */
import { config } from "@/shared/config";

export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details: Record<string, unknown>;
  public readonly correlationId: string | null;

  constructor(input: {
    code: string;
    status: number;
    message: string;
    details?: Record<string, unknown>;
    correlationId?: string | null;
  }) {
    super(input.message);
    this.name = "ApiError";
    this.code = input.code;
    this.status = input.status;
    this.details = input.details ?? {};
    this.correlationId = input.correlationId ?? null;
  }
}

/** Auth-token slot; populated by the auth feature in Milestone 3. */
let authToken: string | null = null;
export const setAuthToken = (token: string | null): void => {
  authToken = token;
};

/** Tenant slot; populated by the workspace store on boot / on switch. */
let workspaceId: string | null = null;
export const setWorkspaceId = (id: string | null): void => {
  workspaceId = id;
};

/** User-id slot; populated by the workspace store on boot. Milestone 2 uses
 *  a header-based dev identity; Milestone 3 replaces this with JWT auth. */
let userId: string | null = null;
export const setUserId = (id: string | null): void => {
  userId = id;
};

const generateCorrelationId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

type HttpOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** Skip prefixing ``config.apiPrefix`` — for absolute paths (rare). */
  raw?: boolean;
  /** Send credentials (cookies) — required for refresh + logout. */
  credentials?: RequestCredentials;
  /** Genesis session id — sends ``x-genesis-session-id`` header so the
   *  backend cost guard forces every downstream reservation into
   *  simulation mode. Only used by /genesis/* calls. */
  genesisSessionId?: string | null;
};

export async function http<T>(path: string, options: HttpOptions = {}): Promise<T> {
  return httpInternal<T>(path, options, /* alreadyRefreshed */ false);
}

/**
 * Patch 37.4.5 — global silent-refresh on 401.
 *
 * Before this patch, only the very first ``/auth/me`` on boot ran the
 * silent-refresh flow. Every other API call — including
 * ``useAnswerBrainQuestion`` — got a raw 401 the moment the 15-minute
 * access token expired. Founders filling out Brain missions saw their
 * answers silently drop (the mutation threw but the UI kept walking
 * them through the questions), then the next navigation surfaced a
 * logout because the store had been cleared out.
 *
 * We now interpose here: on ANY 401 that is NOT itself the refresh /
 * login / logout endpoint, we attempt one silent refresh (deduped by
 * ``attemptSilentRefresh``) and replay the original request with the
 * fresh token. If refresh also fails, we surface the 401 to the
 * caller — same behaviour as before, and the AuthGuard will kick the
 * visitor out on their next render.
 *
 * The refresh cookie is HttpOnly and lasts ``JWT_REFRESH_TTL_DAYS``
 * (30 days), so a founder who stays in the same tab for a whole
 * afternoon will now stay authenticated for the entire brain-fill
 * without ever seeing a login screen.
 */
async function httpInternal<T>(
  path: string,
  options: HttpOptions,
  alreadyRefreshed: boolean,
): Promise<T> {
  const url = options.raw
    ? `${config.backendUrl}${path}`
    : `${config.backendUrl}${config.apiPrefix}${path}`;

  const correlationId = generateCorrelationId();
  const headers: Record<string, string> = {
    "X-Correlation-Id": correlationId,
  };
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  if (!isFormData) headers["Content-Type"] = "application/json";
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  if (workspaceId) headers["X-Workspace-Id"] = workspaceId;
  if (userId) headers["X-User-Id"] = userId;
  if (options.genesisSessionId) headers["x-genesis-session-id"] = options.genesisSessionId;

  const requestInit: RequestInit = {
    method: options.method ?? "GET",
    headers,
    body: options.body === undefined || options.body === null
      ? null
      : isFormData
        ? (options.body as FormData)
        : JSON.stringify(options.body),
    credentials: options.credentials ?? "same-origin",
  };
  if (options.signal) requestInit.signal = options.signal;

  const response = await fetch(url, requestInit);

  const rawBody = await response.text();
  const parsed = rawBody ? safeJson(rawBody) : null;

  if (response.status === 401 && !alreadyRefreshed && !isAuthEndpoint(path)) {
    // Dynamic import avoids a circular dependency between the shared
    // http client and the auth entity that imports it.
    const { attemptSilentRefresh } = await import("@/entities/auth/silentRefresh");
    const refreshed = await attemptSilentRefresh();
    if (refreshed) {
      // Retry ONCE with the new access token. The recursive call
      // sets ``alreadyRefreshed=true`` so a genuinely-invalid token
      // still surfaces the 401 to the caller.
      return httpInternal<T>(path, options, true);
    }
  }

  if (!response.ok) {
    const envelope = (parsed ?? {}) as {
      code?: string;
      message?: string;
      details?: Record<string, unknown>;
      correlation_id?: string | null;
      // FastAPI's default HTTPException shape puts our
      // {code, message} object under ``detail`` — fall back to it
      // so we surface meaningful codes/messages instead of the
      // opaque "Request failed with status N" default.
      detail?: string | {
        code?: string;
        message?: string;
      };
    };
    const detail = typeof envelope.detail === "object" && envelope.detail !== null
      ? envelope.detail
      : undefined;
    const detailStr = typeof envelope.detail === "string"
      ? envelope.detail
      : undefined;
    throw new ApiError({
      code: envelope.code ?? detail?.code ?? "unknown_error",
      status: response.status,
      message:
        envelope.message
          ?? detail?.message
          ?? detailStr
          ?? `Request failed with status ${response.status}`,
      details: envelope.details,
      correlationId: envelope.correlation_id ?? correlationId,
    });
  }

  return parsed as T;
}

/** Paths whose 401s must NOT trigger a silent-refresh recursion —
 *  refresh itself, login, logout, and register. */
function isAuthEndpoint(path: string): boolean {
  return (
    path.startsWith("/auth/refresh")
    || path.startsWith("/auth/login")
    || path.startsWith("/auth/logout")
    || path.startsWith("/auth/register")
    || path.startsWith("/auth/oauth")
  );
}

const safeJson = (body: string): unknown => {
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
};
